import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Trophy, Flame, Coins } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const getBoard = () => api.get("/rewards/leaderboard").then((r) => r.data);
const short = (a) => `${a.slice(0, 6)}…${a.slice(-4)}`;
const MEDALS = ["text-[#D4AF37]", "text-zinc-300", "text-orange-400"];
const TABS = [
  { key: "streaks", label: "Longest streaks", icon: Flame, value: (r) => `${r.streak} day${r.streak === 1 ? "" : "s"}` },
  { key: "earners", label: "Top earners", icon: Coins, value: (r) => `${r.total} FMI` },
];

const Row = ({ r, i, tab, me }) => {
  const mine = me && r.address === me;
  return (
    <li data-testid="leaderboard-row" data-me={mine}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-md border transition-colors ${mine ? "border-[#D4AF37]/60 bg-[#D4AF37]/10" : "border-[#2A2411] bg-[#050505]"}`}>
      <span className={`w-6 text-sm font-bold font-mono ${MEDALS[i] || "text-zinc-600"}`}>{i + 1}</span>
      <Link to={`/address/${r.address}`} className="table-link font-mono text-sm flex-1 truncate">{short(r.address)}</Link>
      {mine && <span className="text-[10px] font-semibold uppercase tracking-wider text-[#D4AF37]">You</span>}
      <span className="font-mono text-sm text-white">{tab.value(r)}</span>
    </li>
  );
};

export const RewardLeaderboard = ({ me }) => {
  const [active, setActive] = useState("streaks");
  const { data } = useQuery({ queryKey: ["rewardLeaderboard"], queryFn: getBoard, refetchInterval: 60000 });
  const tab = TABS.find((t) => t.key === active);
  const rows = data?.[active] || [];
  return (
    <Card data-testid="reward-leaderboard" className="border-[#2A2411] bg-[#0A0A0A]">
      <CardHeader className="pb-3 flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <Trophy size={16} className="text-[#D4AF37]" /> Reward Leaderboard
          {data && <span className="text-[11px] font-normal text-zinc-500">{data.members} {data.members === 1 ? "member" : "members"}</span>}
        </CardTitle>
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`leaderboard-tab-${t.key}`} onClick={() => setActive(t.key)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border transition-colors ${active === t.key ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2A2411] text-zinc-400 hover:text-white"}`}>
              <t.icon size={12} /> {t.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {data && rows.length === 0 && <p data-testid="leaderboard-empty" className="text-sm text-zinc-500">No claims yet. Sign in and be the first on the board!</p>}
        <ol className="space-y-2">{rows.map((r, i) => <Row key={r.address} r={r} i={i} tab={tab} me={me} />)}</ol>
      </CardContent>
    </Card>
  );
};
